import type { IkeRouteFieldMessage } from '@ikegps/core-api';
import { IkeId, IkeRequestError } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { T } from '../../i18n';
import { Services } from '../../services';
import { MobxState } from '../../state/mobx';
import { AppStore } from '../../store';
import { UniversalFileUploadReader } from '../../util/upload.reader';

export class FormImportCtrl extends MobxState {
	fileId: string;

	private static COMMON_SUFFIXES = ['.csv', '.db', '.lccfg', '.client'];
	static ALLOWED_SUFFIXES = [...FormImportCtrl.COMMON_SUFFIXES, '.xlsx', '.dds'];
	static REPLACED_SUFFIXES = [...FormImportCtrl.COMMON_SUFFIXES, '.poleforeman.xlsx'];

	@observable.shallow file: UniversalFileUploadReader<void> = null;

	@observable isActive: boolean = false;

	@observable.shallow validationErrors: IkeRouteFieldMessage[] = [];
	@observable.shallow validationWarnings: IkeRouteFieldMessage[] = [];
	@observable.shallow validationInfo: IkeRouteFieldMessage[] = [];

	secondaryFormId: string | null = null;

	private lastAttachmentId: string | null = null;
	private lastAttachmentName: string | null = null;

	constructor() {
		super();
		makeObservable(this);

		this.fileId = 'file-' + IkeId.generate();
	}

	@action.bound reset() {
		this.file = null;
		this.validationErrors = [];
		this.validationWarnings = [];
		this.validationInfo = [];
		this.lastAttachmentId = null;
		this.lastAttachmentName = null;
	}

	@action.bound openFileDialog(e: MouseEvent) {
		e.preventDefault();

		const fileInput = document.getElementById(this.fileId);
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + this.fileId + "'");
		}

		const evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		fileInput.dispatchEvent(evt);
	}

	@action.bound onFile(e: Event) {
		const fileInput = e.target as HTMLInputElement;
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + this.fileId + "'");
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.FormImportUpload, 'file:click', 'button', 1);
		this.loadFiles(fileInput.files);
	}

	loadFiles(files: FileList | null) {
		this.file = null;

		if (!files || files.length === 0) {
			return;
		}

		const file = files[0];

		const hasAllowedSuffix = FormImportCtrl.ALLOWED_SUFFIXES.some((suffix) => file.name.endsWith(suffix));

		if (!hasAllowedSuffix) {
			NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.FormImportUpload, 'file:count', '1', 1);
		this.file = new UniversalFileUploadReader(file, undefined);
	}

	private getImportName(attachmentName: string): string {
		const regexPattern = FormImportCtrl.REPLACED_SUFFIXES.map((suffix) => '\\' + suffix).join('|');
		const regex = new RegExp(regexPattern, 'g');
		return attachmentName.replace(regex, '');
	}

	@action.bound
	async validateFile() {
		if (this.file && this.file.buffer) {
			const rest = Services.Department.rest;
			try {
				const attachment = await rest.Attachment.set(this.file.name, this.file.buffer, this.file.type);
				this.lastAttachmentId = attachment.objectId;
				this.lastAttachmentName = attachment.name;

				const response = await rest.Form.Pla.import({
					attachmentId: attachment.objectId,
					name: this.getImportName(attachment.name),
					...(this.secondaryFormId && { revision: this.secondaryFormId }),
					validateOnly: true,
				});

				this.validationWarnings = response?.warnings ?? [];
				this.validationInfo = response?.info ?? [];
			} catch (e) {
				if (IkeRequestError.isIkeRequestError(e)) {
					this.validationErrors = e.errors ?? [];
					this.validationWarnings = e.warnings ?? [];
					this.validationInfo = e.info ?? [];
				}
				this.file = null;
				throw e;
			}
		}
	}

	@action.bound
	async createForm() {
		if (!this.lastAttachmentId || !this.lastAttachmentName) {
			throw new Error('No validated attachment to create form from');
		}

		const rest = Services.Department.rest;
		await rest.Form.Pla.import({
			attachmentId: this.lastAttachmentId,
			name: this.getImportName(this.lastAttachmentName),
			...(this.secondaryFormId && { revision: this.secondaryFormId }),
		});

		await AppStore.FormSummary.refresh();
	}

	@action.bound
	async processFile() {
		if (this.file && this.file.buffer) {
			const rest = Services.Department.rest;
			try {
				const attachment = await rest.Attachment.set(this.file.name, this.file.buffer, this.file.type);

				const response = await rest.Form.Pla.import({
					attachmentId: attachment.objectId,
					name: this.getImportName(attachment.name),
					...(this.secondaryFormId && { revision: this.secondaryFormId }),
				});

				this.validationWarnings = response?.warnings ?? [];
				this.validationInfo = response?.info ?? [];

				await AppStore.FormSummary.refresh();
			} catch (e) {
				if (IkeRequestError.isIkeRequestError(e)) {
					this.validationErrors = e.errors ?? [];
					this.validationWarnings = e.warnings ?? [];
					this.validationInfo = e.info ?? [];
				}
				this.file = null;
				throw e;
			}
		}
	}
}
